import StatusIndicator from "components/lib/statusIndicator";
import { differenceInDays, format, isToday } from "date-fns";
import { usePsychologistUser } from "hooks";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import { Typography } from "components/typography";
import { IcbtTask } from "api/types";
import { NotificationDisplayItem } from "./notifications.types";

const ACTION_LABEL_KEYS: Record<IcbtTask["type"], string> = {
  give_feedback: "ICBT_TASK.GIVE_FEEDBACK",
  assessment: "ICBT_TASK.ASSESSMENT",
  extend_due_date: "EDIT_ICBT_FEEDBACK_DATE",
};

const ACTION_ORDER = [
  "give_feedback",
  "assessment",
  "extend_due_date",
] as const satisfies readonly IcbtTask["type"][];

const NotificationEntryContainer = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.PADDING.SM};
  padding: ${({ theme }) => theme.PADDING.MD};
  border-bottom: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};

  &:hover {
    background-color: ${({ theme }) => theme.COLORS.GRAY_X_LIGHT};
    cursor: pointer;

    & p {
      text-decoration: underline;
    }
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
`;

const IconContainer = styled.div`
  width: 24px;
`;

type Props = {
  icbtCommunication: NotificationDisplayItem;
  onClick?: () => void;
};

export const NotificationEntry = ({ icbtCommunication, onClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { psychologist } = usePsychologistUser();
  const feedbackDueDate = new Date(icbtCommunication.feedback_date);
  const patientName = icbtCommunication.patient
    ? `${icbtCommunication.patient.firstName} ${icbtCommunication.patient.lastName}`
    : t("UNKNOWN");

  const overdue = icbtCommunication.overdue;
  const dueToday = isToday(feedbackDueDate);
  const daysPast = differenceInDays(new Date(), feedbackDueDate);

  const dueText = overdue
    ? t("OVERDUE", { days: daysPast })
    : dueToday
      ? t("DUE_TODAY")
      : t("DUE", { date: format(feedbackDueDate, "EEEE, MMM dd") });

  const actionText = ACTION_ORDER.filter((action) =>
    icbtCommunication.actions.includes(action),
  )
    .map((action) => t(ACTION_LABEL_KEYS[action]))
    .join(" & ");

  const handleClick = () => {
    if (!!psychologist.psychologistId && !!icbtCommunication.patient?.userId) {
      onClick?.();
      navigate(
        `/patients/psychologist/${psychologist?.psychologistId}/patient/${icbtCommunication.patient?.userId}/icbt`,
      );
    }
  };

  return (
    <NotificationEntryContainer onClick={handleClick}>
      <IconContainer>
        <StatusIndicator variant={overdue ? "STATUS_RED" : "STATUS_ORANGE"} />
      </IconContainer>
      <TextContainer>
        <TitleRow>
          <Typography variant="body-bold">{patientName}</Typography>
          {actionText ? (
            <Typography insensitive variant="body" color="GRAY_BLACK">
              {`– ${actionText}`}
            </Typography>
          ) : null}
        </TitleRow>
        <Typography variant="body-small" color={overdue ? "ERROR" : "GRAY"}>
          {dueText}
        </Typography>
      </TextContainer>
    </NotificationEntryContainer>
  );
};
